import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Subscription } from '@core/models/subscription.model';

type PaymentBannerState =
  | 'processing'
  | 'action_required'
  | 'payment_method_required'
  | 'canceled'
  | 'healthy'
  | 'none';

@Component({
  selector: 'wai-payment-status-banner',
  templateUrl: './payment-status-banner.component.html',
  styleUrls: ['./payment-status-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaymentStatusBannerComponent {
  @Input() subscription: Subscription | null = null;
  @Input() showHealthyStatus = false;
  @Output() updatePaymentMethod = new EventEmitter<void>();

  public isVisible(): boolean {
    const state = this.getState();
    if (state === 'none') {
      return false;
    }
    if (state === 'healthy') {
      return this.showHealthyStatus;
    }
    return true;
  }

  public getBannerClass(): string {
    switch (this.getState()) {
      case 'action_required':
      case 'payment_method_required':
      case 'canceled':
        return 'warning';
      case 'healthy':
        return 'success';
      case 'processing':
      default:
        return 'info';
    }
  }

  public getIcon(): string {
    switch (this.getState()) {
      case 'action_required':
      case 'canceled':
        return 'warning';
      case 'payment_method_required':
        return 'account_balance';
      case 'healthy':
        return 'check_circle';
      case 'processing':
      default:
        return 'schedule';
    }
  }

  /** Translation key for the banner message. Resolved with `| translate`. */
  public getMessageKey(): string {
    switch (this.getState()) {
      case 'processing':
        // `payment_processing` means the tier is paid for but not yet granted,
        // which is a stronger claim than a generic "settling" notice.
        return this.isAwaitingActivation()
          ? 'INTL_PAYMENT_AWAITING_ACTIVATION'
          : 'INTL_PAYMENT_STATUS_PROCESSING';
      case 'action_required':
        return this.getNormalizedStatus() === 'incomplete_expired'
          ? 'INTL_PAYMENT_COULD_NOT_COMPLETE'
          : 'INTL_PAYMENT_OVERDUE';
      case 'payment_method_required':
        return 'INTL_TRIAL_ADD_BANK_DETAILS_HINT';
      case 'canceled':
        return 'INTL_BILLING_SUBSCRIPTION_CANCELED';
      case 'healthy':
        return 'INTL_PAYMENT_STATUS_HEALTHY';
      default:
        return 'INTL_PAYMENT_STATUS_UNKNOWN';
    }
  }

  /**
   * The reason the bank gave, when there is one. Shown beneath the message so a
   * failure is never silent — QA reported a failed debit with no message at all.
   */
  public getFailureMessage(): string {
    return this.subscription?.paymentFailureMessage || '';
  }

  public shouldShowUpdateAction(): boolean {
    const state = this.getState();
    return state === 'action_required' || state === 'payment_method_required';
  }

  /** Translation key for the action button. Resolved with `| translate`. */
  public getActionLabelKey(): string {
    return this.getState() === 'payment_method_required' ? 'INTL_ADD_BANK_DETAILS' : 'INTL_UPDATE_PAYMENT_METHOD';
  }

  /** Paid, but access is withheld until the SEPA debit clears. */
  private isAwaitingActivation(): boolean {
    return (
      this.subscription?.accessState === 'payment_processing' ||
      this.subscription?.pendingActivation === true
    );
  }

  private getNormalizedStatus(): string {
    // Prefer the backend's access state: Stripe reports a SEPA subscription as
    // `active` while the funds are still in flight and access is withheld.
    const state = this.subscription?.accessState;
    if (state) {
      return state.toLowerCase();
    }
    return (this.subscription?.paymentStatus || '').toLowerCase();
  }

  private getState(): PaymentBannerState {
    const status = this.getNormalizedStatus();
    if (!status) {
      return 'none';
    }
    // SEPA debits settle asynchronously; an `incomplete`/`processing` subscription
    // is simply awaiting clearing and requires no user action.
    if (['incomplete', 'processing', 'payment_processing'].includes(status)) {
      return 'processing';
    }
    if (['past_due', 'unpaid', 'suspended', 'incomplete_expired'].includes(status)) {
      return 'action_required';
    }
    if (status === 'canceled') {
      return 'canceled';
    }
    // A trial started without SEPA details (KAP-1897): ends on Free unless added.
    if (this.subscription?.paymentMethodRequired === true) {
      return 'payment_method_required';
    }
    if (['active', 'trial', 'trialing'].includes(status)) {
      return this.isAwaitingActivation() ? 'processing' : 'healthy';
    }
    return 'none';
  }
}
